import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { adminApi, ApiError } from '../services/adminApi';

const AuthContext = createContext(null);

// Enhanced fingerprint with more device characteristics
const getFingerprint = () => {
  if (typeof window === 'undefined') return '';
  const nav = window.navigator || {};
  const screen = window.screen || {};
  const parts = [
    nav.userAgent || '',
    nav.language || '',
    nav.languages?.join(',') || '',
    `${screen.width}x${screen.height}x${screen.colorDepth || 24}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    nav.hardwareConcurrency || '',
    nav.platform || '',
  ];
  return btoa(parts.join('|'));
};

// Token expiry check utility
const isTokenExpired = (tokenData) => {
  if (!tokenData?.expiresAt) return false;
  return new Date(tokenData.expiresAt) < new Date();
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [usageRemaining, setUsageRemaining] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionCheckFailed, setSessionCheckFailed] = useState(false);

  useEffect(() => {
    let active = true;
    const storedToken =
      typeof window !== 'undefined'
        ? window.localStorage.getItem('cfp_token')
        : null;
    if (storedToken) {
      setToken(storedToken);
    }
    adminApi
      .getSession(storedToken || undefined)
      .then((res) => {
        if (!active) return;
        if (res?.user) {
          setUser(res.user);
        }
      })
      .catch(() => {
        if (!active) return;
        if (storedToken && typeof window !== 'undefined') {
          window.localStorage.removeItem('cfp_token');
        }
        setToken(null);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const fingerprint = getFingerprint();
    const payload = await adminApi.login(email, password, fingerprint);
    
    // Save to state
    setToken(payload.token);
    setUser(payload.user);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('cfp_token', payload.token);
    }
    
    return payload;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const fingerprint = getFingerprint();
    const payload = await adminApi.register(email, password, name, fingerprint);
    
    // Save to state (auto-login after registration)
    setToken(payload.token);
    setUser(payload.user);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('cfp_token', payload.token);
    }
    
    return payload;
  }, []);

  const logout = useCallback(async (showNotification = false) => {
    try {
      await adminApi.logout(token);
    } catch {
      // ignore
    }
    
    // Clear state
    setToken(null);
    setUser(null);
    setUsageRemaining(null);
    setSessionCheckFailed(false);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('cfp_token');
      
      // Show notification if logout was forced
      if (showNotification) {
        window.localStorage.setItem('logout_reason', 'forced');
      }
    }
  }, [token]);

  const recordSearch = useCallback(
    async (term, type) => {
      if (!user) return null;
      try {
        const res = await adminApi.logSearch({ term, type }, token);
        if (typeof res?.remaining === 'number') {
          setUsageRemaining(res.remaining);
        }
        return res;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          await logout();
        }
        // If limit reached (429), throw error to stop search
        if (err instanceof ApiError && err.status === 429) {
          throw err; // Re-throw to be caught by search handler
        }
        // For other errors, log warning and continue
        console.warn('Search log failed', err);
        return null;
      }
    },
    [token, user, logout]
  );

  // Refresh user data periodically to keep session fresh
  const refreshSession = useCallback(async (silent = true) => {
    if (!token) return false;
    try {
      const res = await adminApi.getSession(token);
      if (res?.user) {
        setUser(res.user);
        setSessionCheckFailed(false);
        return true;
      } else {
        throw new Error('Session invalid');
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSessionCheckFailed(true);
        if (!silent) {
          await logout(true); // Show notification on forced logout
        }
        return false;
      }
      return false;
    }
  }, [token, logout]);

  // Real-time session validation - check every 5 seconds
  useEffect(() => {
    if (!token || !user) return;
    
    let checkCount = 0;
    const interval = setInterval(async () => {
      checkCount++;
      const isValid = await refreshSession(true);
      
      // If session is invalid, force logout with notification
      if (!isValid && sessionCheckFailed) {
        await logout(true);
      }
    }, 30000); // Check every 30 seconds (optimized for production)
    
    return () => clearInterval(interval);
  }, [token, user, refreshSession, sessionCheckFailed, logout]);

  // Also keep the 10-minute refresh for session activity
  useEffect(() => {
    if (!token || !user) return;
    const interval = setInterval(() => refreshSession(true), 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token, user, refreshSession]);

  const value = useMemo(
    () => ({
      token,
      user,
      usageRemaining,
      // FIX: Only authenticated when we have verified user data
      isAuthenticated: Boolean(user),
      isLoading,
      login,
      register,
      logout,
      recordSearch,
      refreshSession,
    }),
    [token, user, usageRemaining, isLoading, login, register, logout, recordSearch, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
